// Product events, with no dependencies. Components and pages call these two
// functions. The analytics libraries themselves (posthog-js, Sentry) are
// loaded only by src/pages/_app.tsx, which registers the real
// handlers on window. Keeping the libraries out of every page's module graph
// matters: when pages imported the loader directly, the bundler merged the
// libraries into chunks shared with the chart engine, and the bundle budget
// counted 264 KB and 275 KB on two routes against 35 KB (October 7, 2026).

export type EventProperties = Record<
  string,
  string | number | boolean | null | undefined
>;

export type AnalyticsHandlers = {
  track(event: string, properties?: EventProperties): void;
  identifyMember(memberId: string): void;
};

type Queued =
  | { kind: "track"; event: string; properties?: EventProperties }
  | { kind: "identify"; memberId: string };

declare global {
  interface Window {
    __twtAnalytics?: AnalyticsHandlers;
    __twtAnalyticsQueue?: Queued[];
  }
}

function queue(): Queued[] {
  window.__twtAnalyticsQueue ??= [];
  return window.__twtAnalyticsQueue;
}

/** Records one product event. Never throws, never delays the caller. */
export function track(event: string, properties?: EventProperties): void {
  if (typeof window === "undefined") return;
  try {
    const handlers = window.__twtAnalytics;
    if (handlers) handlers.track(event, properties);
    else queue().push({ kind: "track", event, properties });
  } catch {
    // Analytics never interrupts the page.
  }
}

/** Ties this browser's events to the signed-in member by app_users id. */
export function identifyMember(memberId: string): void {
  if (typeof window === "undefined" || !memberId) return;
  try {
    const handlers = window.__twtAnalytics;
    if (handlers) handlers.identifyMember(memberId);
    else queue().push({ kind: "identify", memberId });
  } catch {
    // Analytics never interrupts the page.
  }
}

/** Called once by the loader: installs the real handlers and replays what
 * was recorded before they existed. */
export function installAnalytics(handlers: AnalyticsHandlers): void {
  if (typeof window === "undefined") return;
  window.__twtAnalytics = handlers;
  const pending = window.__twtAnalyticsQueue ?? [];
  window.__twtAnalyticsQueue = [];
  for (const item of pending) {
    try {
      if (item.kind === "identify") handlers.identifyMember(item.memberId);
      else handlers.track(item.event, item.properties);
    } catch {
      // Analytics never interrupts the page.
    }
  }
}
